import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
    MessageSquare,
    Bug,
    Lightbulb,
    Send,
    Loader2,
    CheckCircle2,
    AlertCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

type FeedbackType = 'bug' | 'feature' | 'feedback';

export default function FeedbackPage() {
    const { t } = useTranslation();
    const [type, setType] = useState<FeedbackType>('feedback');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);

    const submitMutation = useMutation({
        mutationFn: async () => {
            // Create mailto link with feedback content
            const typeLabel = type === 'bug' ? 'Bug Report' : type === 'feature' ? 'Feature Request' : 'Feedback';
            const subject = encodeURIComponent(`[VisionBoard ${typeLabel}] ${title}`);
            const body = encodeURIComponent(
                `Type: ${typeLabel}\n\n` +
                `Title: ${title}\n\n` +
                `Description:\n${description}\n\n` +
                (email ? `Contact Email: ${email}\n\n` : '') +
                `---\nSent from VisionBoard Feedback Form`
            );

            // Open email client
            window.location.href = `mailto:support@1vision.work?subject=${subject}&body=${body}`;

            // Small delay before showing success
            await new Promise(resolve => setTimeout(resolve, 500));
            return { success: true };
        },
        onSuccess: () => {
            setSubmitted(true);
            setTitle('');
            setDescription('');
        },
    });

    const feedbackTypes = [
        {
            value: 'bug' as FeedbackType,
            icon: Bug,
            label: t('feedback.types.bug', 'Bug Report'),
            description: t('feedback.types.bugDesc', 'Report a problem or error'),
            color: 'danger',
        },
        {
            value: 'feature' as FeedbackType,
            icon: Lightbulb,
            label: t('feedback.types.feature', 'Feature Request'),
            description: t('feedback.types.featureDesc', 'Suggest a new feature'),
            color: 'warning',
        },
        {
            value: 'feedback' as FeedbackType,
            icon: MessageSquare,
            label: t('feedback.types.feedback', 'General Feedback'),
            description: t('feedback.types.feedbackDesc', 'Share your thoughts'),
            color: 'primary',
        },
    ];

    const colorClasses = {
        bug: {
            bg: 'bg-danger-50 dark:bg-danger-600/20',
            border: 'border-danger-200 dark:border-danger-600',
            text: 'text-danger-600',
            ring: 'ring-danger-500',
        },
        feature: {
            bg: 'bg-warning-50 dark:bg-warning-600/20',
            border: 'border-warning-200 dark:border-warning-600',
            text: 'text-warning-600',
            ring: 'ring-warning-500',
        },
        feedback: {
            bg: 'bg-primary-50 dark:bg-primary-600/20',
            border: 'border-primary-200 dark:border-primary-600',
            text: 'text-primary-600',
            ring: 'ring-primary-500',
        },
    };

    if (submitted) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="card p-12 text-center">
                    <CheckCircle2 className="w-16 h-16 text-success-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold mb-2">
                        {t('feedback.thankYou', 'Thank you!')}
                    </h2>
                    <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                        {t('feedback.thankYouDesc', 'Your feedback has been submitted successfully. We appreciate your input!')}
                    </p>
                    <button
                        onClick={() => setSubmitted(false)}
                        className="btn-primary"
                    >
                        {t('feedback.submitAnother', 'Submit another')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold">{t('feedback.title', 'Feedback & Support')}</h1>
                <p className="text-neutral-600 dark:text-neutral-400">
                    {t('feedback.subtitle', 'Help us improve VisionBoard by sharing your thoughts')}
                </p>
            </div>

            {/* Feedback Type Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {feedbackTypes.map((item) => (
                    <button
                        key={item.value}
                        onClick={() => setType(item.value)}
                        className={clsx(
                            'p-4 rounded-xl border-2 transition-all text-left',
                            type === item.value
                                ? `${colorClasses[item.value].bg} ${colorClasses[item.value].border} ring-2 ${colorClasses[item.value].ring}`
                                : 'bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 hover:border-neutral-300'
                        )}
                    >
                        <item.icon className={clsx('w-6 h-6 mb-2', colorClasses[item.value].text)} />
                        <p className="font-medium text-sm">{item.label}</p>
                        <p className="text-xs text-neutral-500 mt-1">{item.description}</p>
                    </button>
                ))}
            </div>

            {/* Feedback Form */}
            <div className="card p-6 space-y-4">
                {/* Title */}
                <div>
                    <label className="label">
                        {type === 'bug'
                            ? t('feedback.bugTitle', 'What went wrong?')
                            : type === 'feature'
                                ? t('feedback.featureTitle', 'What feature would you like?')
                                : t('feedback.feedbackTitle', 'Title')}
                    </label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="input"
                        placeholder={
                            type === 'bug'
                                ? t('feedback.bugPlaceholder', 'e.g., Task drag and drop not working')
                                : type === 'feature'
                                    ? t('feedback.featurePlaceholder', 'e.g., Add recurring tasks')
                                    : t('feedback.feedbackPlaceholder', 'Brief summary')
                        }
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="label">
                        {t('feedback.description', 'Description')}
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="input min-h-[150px]"
                        placeholder={
                            type === 'bug'
                                ? t('feedback.bugDescPlaceholder', 'Please describe the issue in detail. What were you trying to do? What happened instead?')
                                : type === 'feature'
                                    ? t('feedback.featureDescPlaceholder', 'Describe the feature and how it would help you...')
                                    : t('feedback.feedbackDescPlaceholder', 'Share your thoughts, suggestions, or any feedback...')
                        }
                    />
                </div>

                {/* Email (optional) */}
                <div>
                    <label className="label flex items-center gap-2">
                        {t('feedback.email', 'Email')}
                        <span className="text-xs text-neutral-400 font-normal">
                            ({t('feedback.optional', 'optional')})
                        </span>
                    </label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input"
                        placeholder={t('feedback.emailPlaceholder', 'your@email.com - if you want a response')}
                    />
                </div>

                {/* Info Box */}
                {type === 'bug' && (
                    <div className="flex gap-3 p-3 bg-primary-50 dark:bg-primary-600/20 rounded-lg">
                        <AlertCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-primary-700 dark:text-primary-300">
                            <p className="font-medium">{t('feedback.bugTip', 'Tip for bug reports:')}</p>
                            <p className="mt-1 text-primary-600 dark:text-primary-400">
                                {t('feedback.bugTipDesc', 'Include steps to reproduce, expected vs actual behavior, and your browser/device info if possible.')}
                            </p>
                        </div>
                    </div>
                )}

                {/* Submit */}
                <div className="flex justify-end pt-2">
                    <button
                        onClick={() => submitMutation.mutate()}
                        disabled={!title.trim() || !description.trim() || submitMutation.isPending}
                        className="btn-primary"
                    >
                        {submitMutation.isPending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                {t('feedback.submitting', 'Submitting...')}
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                {t('feedback.submit', 'Submit Feedback')}
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Contact Info */}
            <div className="text-center text-sm text-neutral-500">
                <p>
                    {t('feedback.contact', 'For urgent issues, contact us at')}{' '}
                    <a href="mailto:support@1vision.work" className="text-primary-600 hover:underline">
                        support@1vision.work
                    </a>
                </p>
            </div>
        </div>
    );
}
